import React, { useEffect, useState } from 'react';
import { MarkerData } from '../../components/LandscapeMap/LandscapeMapTypes';
import LandscapeMap from '../../components/LandscapeMap/LandscapeMap';
import { Col } from 'reactstrap';
import './LandscapeMapContainer.scss';
import L, { LatLngBounds } from 'leaflet';
import { MAP_VECTOR_STYLES_URL } from '../../utils/config';

export enum Region {
    Mainland = 'mainland',
    Alaska = 'alaska',
    Hawaii = 'hawaii',
}

export const regionConfigs: Record<
    Region,
    {
        bounds: LatLngBounds;
        minZoom: number;
        minSubMapZoom: number;
        maxZoom: number;
        defaultZoom: number;
        minimapMarkersSize: number;
    }
> = {
    [Region.Mainland]: {
        bounds: new L.LatLngBounds([23, -125], [51, -66]),
        minZoom: 3.5,
        minSubMapZoom: 1,
        maxZoom: 16,
        defaultZoom: 4,
        minimapMarkersSize: 100000,
    },
    [Region.Alaska]: {
        bounds: new L.LatLngBounds([51, -180], [72, -123]),
        minZoom: 4,
        minSubMapZoom: 1,
        maxZoom: 16,
        defaultZoom: 4,
        minimapMarkersSize: 150000,
    },
    [Region.Hawaii]: {
        bounds: new L.LatLngBounds([18.5, -161], [22.3, -154]),
        minZoom: 4,
        minSubMapZoom: 4,
        maxZoom: 16,
        defaultZoom: 7,
        minimapMarkersSize: 25000,
    },
};

export interface LandscapeMapContainerProps {
    markers: MarkerData[];
    selectedMarkers: MarkerData[];
    setSelectedMarkers: (markers: MarkerData[]) => void;
    isFullScreen: boolean;
    setIsFullScreen: (isFullScreen: boolean) => void;
    setMarkers: (markers: MarkerData[]) => void;
    markerVisibilityZoom: number;
    colMd: number;
    renderColorsDescription: () => React.JSX.Element[];
    renderShapesDescription: () => React.JSX.Element[];
    setCurrentZoom: (zoom: number) => void;
    currentZoom: number;
    mapHeader?: string | React.JSX.Element;
}

const LandscapeMapContainer = (props: LandscapeMapContainerProps) => {
    const [mainRegion, setMainRegion] = useState<Region>(Region.Mainland);
    const [selectedMarkers, setSelectedMarkers] = useState<MarkerData[]>([]);

    useEffect(() => {
        const mainRegionConfig = regionConfigs[mainRegion];
        props.setCurrentZoom(mainRegionConfig.defaultZoom);
    }, [mainRegion]);

    useEffect(() => {
        if (props.selectedMarkers.length < 1) {
            return;
        }

        const wereNewMarkersSelected = !selectedMarkers.some((sm) =>
            props.selectedMarkers.some((psm) => psm.id === sm.id)
        );

        if (!wereNewMarkersSelected) {
            return;
        }

        setSelectedMarkers(props.selectedMarkers);

        const firstSelectedMarker = props.selectedMarkers.find(
            (x) => x != null
        );
        if (!firstSelectedMarker) {
            return;
        }
        const pos = L.latLng(
            firstSelectedMarker.position[0],
            firstSelectedMarker.position[1]
        );

        let newRegion = mainRegion;
        let newCurrentZoom = props.currentZoom;

        if (regionConfigs[Region.Alaska].bounds.contains(pos)) {
            newRegion = Region.Alaska;
            newCurrentZoom = regionConfigs[Region.Alaska].defaultZoom;
        } else if (regionConfigs[Region.Hawaii].bounds.contains(pos)) {
            newRegion = Region.Hawaii;
            newCurrentZoom = regionConfigs[Region.Hawaii].defaultZoom;
        } else {
            newRegion = Region.Mainland;
            newCurrentZoom = regionConfigs[Region.Mainland].defaultZoom;
        }
        if (newRegion !== mainRegion) {
            setMainRegion(newRegion);
            props.setCurrentZoom(newCurrentZoom);
        }
    }, [props.selectedMarkers]);

    const renderMainMap = () => {
        const cfg = regionConfigs[mainRegion];
        return (
            <LandscapeMap
                key={mainRegion}
                mapStylesUrl={MAP_VECTOR_STYLES_URL}
                selectedMarkers={props.selectedMarkers}
                markers={props.markers}
                defaultZoom={cfg.defaultZoom}
                currentZoom={props.currentZoom}
                setCurrentZoom={(z) => props.setCurrentZoom(z)}
                markerVisibilityZoom={props.markerVisibilityZoom}
                isFullscreen={props.isFullScreen}
                setIsFullscreen={props.setIsFullScreen}
                bounds={cfg.bounds}
                minZoom={cfg.minZoom}
                maxZoom={cfg.maxZoom}
                isSubMap={false}
                subMaps={renderSubMaps}
                renderColorsDescription={props.renderColorsDescription}
                renderShapesDescription={props.renderShapesDescription}
            />
        );
    };

    const renderSubMaps = () => {
        const others = Object.values(Region).filter((r) => r !== mainRegion);
        return others.map((region, index) => {
            const cfg = regionConfigs[region];
            const leftOffset = 10 + index * 70;
            const markersSize = cfg.minimapMarkersSize;
            return (
                <div
                    key={region}
                    className="sub-map"
                    style={{ left: leftOffset }}
                    onClick={() => {
                        setMainRegion(region);
                        props.setCurrentZoom(cfg.defaultZoom);
                    }}>
                    <LandscapeMap
                        mapStylesUrl={MAP_VECTOR_STYLES_URL}
                        selectedMarkers={props.selectedMarkers}
                        markers={props.markers}
                        defaultZoom={cfg.defaultZoom}
                        currentZoom={cfg.defaultZoom}
                        setCurrentZoom={() => {}}
                        markerVisibilityZoom={props.markerVisibilityZoom}
                        isFullscreen={false}
                        setIsFullscreen={() => {}}
                        bounds={cfg.bounds}
                        minZoom={cfg.minSubMapZoom}
                        maxZoom={cfg.maxZoom}
                        isSubMap={true}
                        markersSize={markersSize}
                    />
                </div>
            );
        });
    };

    return (
        <Col className="landscape-map-column" md={props.colMd}>
            {props.mapHeader ?? <></>}
            <div className="landscape-map" style={{ position: 'relative' }}>
                {renderMainMap()}
            </div>
        </Col>
    );
};

export default LandscapeMapContainer;
